import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Tenant } from '@core/models/tenant.model';
import { User } from '@core/models/user.model';
import { environment } from '@environment/environment';
import {
  BehaviorSubject,
  Observable,
  Subscription,
  catchError,
  map,
  of,
  throwError,
} from 'rxjs';
import { tap } from 'rxjs/operators';
@Injectable()
export class AuthService {
  loggedUser = new BehaviorSubject<User | undefined>(undefined);
  myLastUser: any = undefined;

  authEventFromOther = new BroadcastChannel('loggedUser');
  logoutFromOther = new BroadcastChannel('logoutFromOther');

  finishedLogout = new BehaviorSubject<any>(null);
  getUserInfoSubscription: Subscription | undefined;
  isExternalUser = false;
  isAnonymousUser = false;

  constructor(private httpClient: HttpClient) {
    this.authEventFromOther.onmessage = (message) =>
      this.handleOtherTabLogin(message);
    this.logoutFromOther.onmessage = (message) =>
      this.handleOtherTabLogin(message);
    const prevData = localStorage.getItem('loggedUser');
    if (prevData) {
      this.myLastUser = JSON.parse(prevData);
    } else {
      this.myLastUser = null;
    }
    if (window.location.pathname.includes('/exam_open_access/')) {
      this.isExternalUser = true;
      if (!window.location.search.includes('&receiver_id')) {
        this.isAnonymousUser = true;
      }
    }
  }

  handleOtherTabLogin(message: any) {
    const fromUser = message.data;
    if (fromUser?.id == this.myLastUser?.id) return;
    window.location.href = 'auth/login';
  }

  login(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/login`;

    const fd = new FormData();
    fd.append('username', data.email);
    fd.append('password', data.password);

    return this.httpClient
      .post(url, fd, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => error)),
        tap((data: any) => {
          this.setLoggedUser(data.user);
          this.authEventFromOther.postMessage(data.user);
        }),
      );
  }

  getLoggedUser(): User | undefined {
    if (this.isExternalUser && !this.loggedUser.value) {
      if (!this.getUserInfoSubscription) {
        this.getUserInfoSubscription = this.getUserInfo().subscribe((user) => {
          this.setLoggedUser(user);
          this.getUserInfoSubscription?.unsubscribe();
        });
      }
      // Retornando um usuário temporário para não parar de tentar conectar nos websockets até retornar o usuário real
      const user = {
        token: 'temp_token',
        email: 'anonymous@anonymous.com',
      } as User;
      return user;
    }

    let user = this.loggedUser.value;
    if (!user) {
      const userJson = localStorage.getItem('loggedUser');
      if (userJson) {
        user = JSON.parse(userJson);
      }
    }
    return user;
  }

  setLoggedUser(user: User | undefined) {
    if (user) {
      localStorage.setItem('loggedUser', JSON.stringify(user));
    } else {
      localStorage.removeItem('loggedUser');
    }
    this.loggedUser.next(user);
  }

  logout(): Observable<any> {
    const url = `${environment.examInfoBackend}/logout`;

    return this.httpClient
      .post(url, {
        withCredentials: true,
      })
      .pipe(
        catchError((error) => throwError(() => error)),
        tap(() => {
          localStorage.removeItem('latestUrl');
          this.setLoggedUser(undefined);
          this.finishedLogout.next(null);

          this.logoutFromOther.postMessage(this.myLastUser);
          this.myLastUser = undefined;
        }),
      );
  }

  createUser(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/create_user`;
    if (Object.prototype.hasOwnProperty.call(data, 'confirm_password')) {
      delete data.confirm_password;
    }
    return this.httpClient
      .post(url, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  createExternalUser(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/create_external_user`;
    return this.httpClient
      .post(url, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  acceptInvitation(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/accept_invitation`;
    if (Object.prototype.hasOwnProperty.call(data, 'confirm_password')) {
      delete data.confirm_password;
    }
    return this.httpClient
      .post(url, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  createInvitation(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/create_invitation`;
    return this.httpClient
      .post(url, data)
      .pipe(catchError((error) => throwError(() => error)));
  }

  sendResetPasswordRequest(data: any): Observable<any> {
    const url = `${environment.examInfoBackend}/reset`;

    const fd = new FormData();
    fd.append('email', data.email);

    return this.httpClient
      .post(url, fd, {
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  checkTokenValidity(token: string): Observable<any> {
    const url = `${environment.examInfoBackend}/reset`;

    return this.httpClient
      .get(url, {
        params: { token },
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  checkInvitationCodeValidity(invitation_code: string): Observable<any> {
    const url = `${environment.examInfoBackend}/invitation_code`;

    return this.httpClient
      .get(url, {
        params: { invitation_code },
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  changePassword(password: string, token: string): Observable<any> {
    const url = `${environment.examInfoBackend}/reset`;

    const fd = new FormData();
    fd.append('password', password);
    fd.append('token', token);

    return this.httpClient
      .patch(url, fd, {
        params: { token },
        withCredentials: true,
      })
      .pipe(catchError((error) => throwError(() => error)));
  }

  getUserInfo(): Observable<User> {
    const route = `${environment.examInfoBackend}/user_info`;
    return this.httpClient
      .get<User>(route)
      .pipe(catchError((error) => throwError(() => error)));
  }

  getTenant(): Observable<Tenant> {
    const user = this.getLoggedUser();
    if (
      user !== null &&
      user !== undefined &&
      user?.tenant !== undefined &&
      user?.tenant !== null
    ) {
      return of(user.tenant);
    }
    return this.getUserInfo().pipe(map((u: any) => u.tenant));
  }

  hasKFBIO(currentTenant: Tenant | null) {
    if (currentTenant === null) return false;
    return (
      currentTenant?.kfbio_path !== undefined &&
      currentTenant?.kfbio_path?.length > 0
    );
  }

  KFBIOExamPath(currentTenant: Tenant, exam_code: string) {
    return `${currentTenant.kfbio_path}?caseid=${exam_code}`;
  }

  KFBIOSlidePath(currentTenant: Tenant, slide_code: string) {
    return `${currentTenant.kfbio_path}?pid=${slide_code}`;
  }

  MOTICExamPath(currentTenant: Tenant, exam_code: string) {
    return ``;
  }

  MOTICSlidePath(currentTenant: Tenant, slide_code: string) {
    return ``;
  }

  hasMOTIC(currentTenant: Tenant | null) {
    if (currentTenant === null) return false;
    return (
      currentTenant?.motic_path !== undefined &&
      currentTenant?.motic_path?.length > 0
    );
  }
}
